import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, FolderOpen, Loader2, RefreshCw, Unplug } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../../lib/api";
import { formatTime } from "../../lib/format";
import { DriveFolderPicker } from "./DriveFolderPicker";
import { DriveLinkDialog } from "./DriveLinkDialog";
import { DriveDryRun } from "./DriveDryRun";
import { DriveSync } from "./DriveSync";

const RETURN_MSG = { cancelado: "No se conectó: cancelaste el inicio de sesión de Google.", error: "Google no aceptó la conexión; intenta de nuevo.",
  permiso: "Falta el permiso para leer tu Drive: vuelve a conectar y marca la casilla de Google Drive." };

const DriveLogo = () => (
  <svg viewBox="0 0 87.3 78" className="h-9 w-9 shrink-0" aria-hidden="true">
    <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da" />
    <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47" />
    <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335" />
    <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d" />
    <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc" />
    <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00" />
  </svg>
);

const Confirm = ({ status, onDone }) => {
  const oauth = status.mode === "oauth";
  const [folder, setFolder] = useState(null);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/drive/folder").then((r) => setFolder(r.data))
      .catch((e) => { toast.error(errorMessage(e, "No se encontró la carpeta")); setFolder({ name: "" }); });
  }, []);
  const confirm = async () => {
    setBusy(true);
    try { onDone((await api.post("/drive/confirm", folder.token ? { folder: folder.token } : {})).data); toast.success("Carpeta confirmada"); }
    catch (e) { toast.error(errorMessage(e, "No se pudo confirmar la carpeta")); }
    setBusy(false);
  };
  return (
    <div className="space-y-3" data-testid="drive-confirm">
      <p className="text-sm font-bold text-[#182235]">Confirmar carpeta</p>
      <div className="flex items-center gap-2 rounded-xl border-2 border-[#0f5ef7] bg-[#f2f7ff] px-3 py-2.5 text-sm font-semibold" data-testid="drive-confirm-folder">
        <FolderOpen className="h-5 w-5 text-[#0f5ef7]" />{folder ? (folder.name || "Elige una carpeta") : <Loader2 className="h-4 w-4 animate-spin" />}
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={confirm} disabled={busy || !folder?.name} className="tr-btn-primary px-4 py-2 text-sm" data-testid="drive-confirm-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Confirmar
        </button>
        <button type="button" onClick={() => setPicking(true)} className="tr-btn-light px-4 py-2 text-sm" data-testid="drive-change-folder">Cambiar carpeta</button>
      </div>
      {status.folder_confirmed && folder?.token && <p className="text-xs font-semibold text-[#b54708]" data-testid="drive-change-warning">Al confirmar, los documentos de la carpeta anterior se quitan de la plataforma y se archivan los de la nueva.</p>}
      {oauth
        ? <DriveFolderPicker open={picking} onClose={() => setPicking(false)} onPick={(f) => { setFolder(f); setPicking(false); }} />
        : <DriveLinkDialog open={picking} onClose={() => setPicking(false)} onPick={(f) => { setFolder(f); setPicking(false); }} />}
      <DriveDryRun />
    </div>
  );
};

const Connected = ({ status, onChange, onDisconnect, onSynced }) => (
  <div className="space-y-3">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="flex flex-wrap items-center gap-2 text-sm" data-testid="drive-status-badge">
        <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(19,182,122,0.12)] px-2.5 py-1 text-xs font-bold text-[#0f8b5f]"><CheckCircle2 className="h-3.5 w-3.5" /> Conectado</span>
        <span>{status.email && <>como <b>{status.email}</b> · </>}Carpeta <b>{status.folder_name}</b> · Última sincronización {status.last_sync_at ? formatTime(status.last_sync_at) : "aún no"}</span>
      </p>
      <div className="flex gap-2">
        <button type="button" onClick={onChange} className="tr-btn-light px-3 py-1.5 text-sm" data-testid="drive-change-folder-connected"><FolderOpen className="h-4 w-4" /> Cambiar carpeta</button>
        <button type="button" onClick={onDisconnect} className="tr-btn-light px-3 py-1.5 text-sm" data-testid="drive-disconnect"><Unplug className="h-4 w-4" /> Desconectar</button>
      </div>
    </div>
    <DriveSync onFinished={onSynced} mode={status.mode} />
    <DriveDryRun />
  </div>
);

export const DriveCard = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [changing, setChanging] = useState(false);
  const [params, setParams] = useSearchParams();
  const ret = params.get("drive");
  const load = useCallback(() => api.get("/drive/status").then((r) => setStatus(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!ret) return;
    if (RETURN_MSG[ret]) toast.error(RETURN_MSG[ret]);
    if (ret === "confirmar") setChanging(true);
    params.delete("drive");
    setParams(params, { replace: true });
  }, [ret, params, setParams]);

  const connect = async () => {
    setBusy(true);
    try { window.location.assign((await api.post("/drive/connect")).data.authorization_url); }
    catch (e) { toast.error(errorMessage(e, "No se pudo abrir Google")); setBusy(false); }
  };
  const disconnect = async () => {
    try { setStatus((await api.post("/drive/disconnect")).data); toast.success("Google Drive desconectado"); } catch (e) { toast.error(errorMessage(e, "No se pudo desconectar")); }
  };
  if (!status) return null;
  const oauth = status.mode === "oauth";
  const linked = oauth ? status.connected && !status.needs_reconnect : status.configured;
  const confirming = linked && (changing || !status.folder_confirmed);

  return (
    <section className="tr-card space-y-3 p-5" data-testid="drive-card">
      <div className="flex flex-wrap items-center gap-4">
        <DriveLogo />
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-extrabold text-[#182235]">{status.needs_reconnect ? "Reconecta Google Drive" : "Google Drive"}</h2>
          {!linked && <p className="text-sm text-[#5b667a]" data-testid="drive-explainer">
            {status.needs_reconnect ? "Google dejó de dar acceso. Vuelve a conectar; ningún documento se borró." : oauth ? "Un clic: inicia sesión con tu cuenta de Google y la plataforma leerá tu carpeta de expedientes. Tus archivos se quedan en tu Drive." : "La plataforma lee tu carpeta de expedientes de Drive. Tus archivos se quedan en tu Drive."}
          </p>}
        </div>
        {!linked && oauth && (
          <button type="button" onClick={connect} disabled={busy || !status.configured} className="tr-btn-primary px-5 py-3 text-base" data-testid="drive-connect-button">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <RefreshCw className="h-5 w-5" />} {status.needs_reconnect ? "Reconectar Google Drive" : "Conectar Google Drive"}
          </button>
        )}
      </div>
      {!status.configured && <p className="text-xs font-semibold text-[#b54708]" data-testid="drive-not-configured">{oauth ? "Falta guardar el Client ID y el Client secret de Google para activar este botón." : "Falta guardar la Clave de API de Google para leer la carpeta."}</p>}
      {status.unreadable && <p className="rounded-lg bg-[#fef3f2] px-3 py-2 text-sm font-semibold text-[#b42318]" data-testid="drive-unreadable">{status.unreadable_message}</p>}
      {confirming && <Confirm status={status} onDone={(s) => { setStatus(s); setChanging(false); }} />}
      {linked && !confirming && <Connected status={status} onChange={() => setChanging(true)} onDisconnect={disconnect} onSynced={load} />}
    </section>
  );
};
